import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp, Eye, EyeOff, Plus, Radio, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { MarketingContent, MarketingSurfaceType } from '@/api/admin-marketing-content-types'
import { MarketingSurface } from '@/api/admin-marketing-content-types'
import { useReplaceChannelLayout } from '@/api/api-mutations'
import {
	useGetChannelLayout,
	useGetChannelResolvedContent,
	useListMarketingChannels,
	useListMarketingContent
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'

import { FeedPreview } from './-preview'

export const Route = createFileRoute('/_authenticated/_general/marketing/layout/')({
	component: FeedLayoutPage,
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.ContentLayout.Title()
	}
})

// Every surface a channel layout can be edited for, with the label shown in the selector.
const useSurfaceLabels = (): Record<MarketingSurfaceType, string> => {
	const { L } = useI18n()
	return {
		[MarketingSurface.CustomerAppFeed]: L.Marketing.Surface.CustomerAppFeed(),
		[MarketingSurface.CheckoutScreen]: L.Marketing.Surface.CheckoutScreen()
	}
}

function FeedLayoutPage() {
	const { L } = useI18n()
	const surfaceLabels = useSurfaceLabels()
	const channelsQuery = useListMarketingChannels()
	const channels = channelsQuery.data ?? []

	const [selectedChannelUuid, setSelectedChannelUuid] = useState<string | null>(null)
	const [selectedSurface, setSelectedSurface] = useState<MarketingSurfaceType>(MarketingSurface.CustomerAppFeed)

	// Default to the first channel once channels load; keep a valid selection thereafter.
	useEffect(() => {
		if (channels.length === 0) return
		if (!selectedChannelUuid || !channels.some(c => c.uuid === selectedChannelUuid)) {
			setSelectedChannelUuid(channels[0].uuid)
		}
	}, [channels, selectedChannelUuid])

	if (channelsQuery.isLoading) {
		return (
			<SingleColumnPage>
				<Container className='flex flex-1 items-center justify-center'>
					<Text className='text-ui-fg-subtle'>{L.General.Loading()}</Text>
				</Container>
			</SingleColumnPage>
		)
	}

	if (channels.length === 0) {
		return (
			<SingleColumnPage>
				<Container className='flex flex-1 flex-col overflow-hidden'>
					<ContainerHeader heading={L.Marketing.ContentLayout.Title()} />
					<div className='flex flex-1 items-center justify-center p-6'>
						<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
							<Radio className='size-10 text-ui-fg-muted' />
							<Heading level='h3'>{L.Marketing.Channel.NoChannels()}</Heading>
							<Text className='max-w-sm text-ui-fg-subtle'>{L.Marketing.ContentLayout.NoChannels()}</Text>
							<Link to='/settings/marketing-channels'>
								<Button>{L.Marketing.ContentLayout.GoToChannels()}</Button>
							</Link>
						</div>
					</div>
				</Container>
			</SingleColumnPage>
		)
	}

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Marketing.ContentLayout.Title()}
					rightSide={
						<div className='flex items-center gap-4'>
							{/* Channel (broad) then Surface (a specific view within that channel) - each
							    channel×surface pair has its own independent layout. */}
							<label className='flex items-center gap-1.5 text-xs text-ui-fg-subtle'>
								<span className='hidden sm:inline'>{L.Marketing.ContentLayout.SelectChannel()}</span>
								<select
									value={selectedChannelUuid ?? ''}
									onChange={e => setSelectedChannelUuid(e.target.value)}
									className='h-9 rounded-md border border-ui-border-base bg-ui-bg-base px-2 text-sm'
									aria-label={L.Marketing.ContentLayout.SelectChannel()}
								>
									{channels.map(c => (
										<option key={c.uuid} value={c.uuid}>
											{c.name}
										</option>
									))}
								</select>
							</label>
							<label className='flex items-center gap-1.5 text-xs text-ui-fg-subtle'>
								<span className='hidden sm:inline'>{L.Marketing.Surface.Label()}</span>
								<select
									value={selectedSurface}
									onChange={e => setSelectedSurface(e.target.value as MarketingSurfaceType)}
									className='h-9 rounded-md border border-ui-border-base bg-ui-bg-base px-2 text-sm'
									aria-label={L.Marketing.Surface.Label()}
								>
									{Object.values(MarketingSurface).map(surface => (
										<option key={surface} value={surface}>
											{surfaceLabels[surface]}
										</option>
									))}
								</select>
							</label>
						</div>
					}
				/>
				{selectedChannelUuid && (
					<ChannelLayoutEditor
						key={`${selectedChannelUuid}:${selectedSurface}`}
						channelUuid={selectedChannelUuid}
						surface={selectedSurface}
					/>
				)}
			</Container>
		</SingleColumnPage>
	)
}

function ChannelLayoutEditor({ channelUuid, surface }: { channelUuid: string; surface: MarketingSurfaceType }) {
	const { L } = useI18n()

	const layoutQuery = useGetChannelLayout({ marketingChannelUuid: channelUuid }, { surface })
	const contentQuery = useListMarketingContent({ limit: 100, offset: 0 })
	const replaceLayout = useReplaceChannelLayout(true, channelUuid)

	const [layoutUuids, setLayoutUuids] = useState<string[]>([])
	const [dirty, setDirty] = useState(false)
	const [previewOpen, setPreviewOpen] = useState(true)

	const dirtyRef = useRef(false)
	const setDirtyBoth = (v: boolean) => {
		dirtyRef.current = v
		setDirty(v)
	}

	// Seed from the saved layout; don't clobber in-progress edits on background refetches.
	useEffect(() => {
		const layout = layoutQuery.data
		if (!layout) return
		if (dirtyRef.current) return
		setLayoutUuids(layout.entries.map(e => e.content.uuid))
		setDirtyBoth(false)
	}, [layoutQuery.data])

	const feedQuery = useGetChannelResolvedContent(
		{ marketingChannelUuid: channelUuid },
		{ surface },
		{ enabled: previewOpen }
	)

	const contentByUuid = useMemo(() => {
		const map = new Map<string, MarketingContent>()
		for (const c of contentQuery.data?.content ?? []) map.set(c.uuid, c)
		return map
	}, [contentQuery.data])

	const available = useMemo(
		() => (contentQuery.data?.content ?? []).filter(c => !layoutUuids.includes(c.uuid)),
		[contentQuery.data, layoutUuids]
	)

	const move = (index: number, direction: -1 | 1) => {
		const next = index + direction
		if (next < 0 || next >= layoutUuids.length) return
		const copy = [...layoutUuids]
		;[copy[index], copy[next]] = [copy[next], copy[index]]
		setLayoutUuids(copy)
		setDirtyBoth(true)
	}

	const add = (uuid: string) => {
		setLayoutUuids([...layoutUuids, uuid])
		setDirtyBoth(true)
	}

	const remove = (uuid: string) => {
		setLayoutUuids(layoutUuids.filter(u => u !== uuid))
		setDirtyBoth(true)
	}

	const handleSave = async () => {
		try {
			const result = await replaceLayout.mutateAsync({
				path: { marketingChannelUuid: channelUuid },
				query: { surface },
				body: { contentUuids: layoutUuids }
			})
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			setDirtyBoth(false)
			toast.success(L.Marketing.ContentLayout.Saved())
			if (previewOpen) void feedQuery.refetch()
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	if (layoutQuery.isLoading) {
		return (
			<div className='flex flex-1 items-center justify-center p-6'>
				<Text className='text-ui-fg-subtle'>{L.General.Loading()}</Text>
			</div>
		)
	}

	return (
		<div className='flex flex-1 gap-6 overflow-hidden'>
			<div className='flex-1 overflow-y-auto p-4 sm:p-6'>
				<div className='flex flex-col gap-6'>
					<div className='flex items-start justify-between gap-4'>
						<Text className='max-w-2xl text-xs text-ui-fg-muted'>{L.Marketing.ContentLayout.Help()}</Text>
						<IconButton
							tooltipText={L.Marketing.ContentLayout.PreviewTitle()}
							onClick={() => setPreviewOpen(o => !o)}
							variant='transparent'
							className='hidden lg:inline-flex'
						>
							{previewOpen ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
						</IconButton>
					</div>

					<section className='flex flex-col gap-2'>
						<Heading level='h2'>{L.Marketing.ContentLayout.InLayoutTitle()}</Heading>
						{layoutUuids.length === 0 ? (
							<div className='rounded-md border border-dashed border-ui-border-base p-8 text-center'>
								<Text className='text-sm text-ui-fg-subtle'>{L.Marketing.ContentLayout.Empty()}</Text>
							</div>
						) : (
							<div className='flex flex-col gap-1.5'>
								{layoutUuids.map((uuid, index) => {
									const content = contentByUuid.get(uuid)
									return (
										<div
											key={uuid}
											className='flex items-center gap-3 rounded-md border border-ui-border-base bg-ui-bg-base px-3 py-2.5'
										>
											<div className='flex flex-col'>
												<button
													type='button'
													className='text-ui-fg-muted hover:text-ui-fg-base disabled:opacity-30'
													disabled={index === 0}
													onClick={() => move(index, -1)}
													aria-label={L.Marketing.Campaign.Action.MoveUp()}
												>
													<ChevronUp className='size-3' />
												</button>
												<button
													type='button'
													className='text-ui-fg-muted hover:text-ui-fg-base disabled:opacity-30'
													disabled={index === layoutUuids.length - 1}
													onClick={() => move(index, 1)}
													aria-label={L.Marketing.Campaign.Action.MoveDown()}
												>
													<ChevronDown className='size-3' />
												</button>
											</div>
											<div className='flex flex-1 flex-col'>
												<Link
													to='/marketing/content/$contentUuid'
													params={{ contentUuid: uuid }}
													className='font-medium hover:underline'
												>
													{content?.name ?? uuid}
												</Link>
												{content && (
													<Text className='whitespace-nowrap text-xs text-ui-fg-subtle'>
														{L.Marketing.Campaign.StatusEnum(content.status)}
														{' · '}
														{L.Marketing.Content.BlockCount({ count: content.blockCount })}
													</Text>
												)}
											</div>
											<IconButton
												variant='transparent'
												size='small'
												tooltipText={L.Marketing.ContentLayout.Remove()}
												onClick={() => remove(uuid)}
											>
												<Trash2 className='size-4' />
											</IconButton>
										</div>
									)
								})}
							</div>
						)}
						<div className='flex justify-end'>
							<Button onClick={handleSave} disabled={!dirty || replaceLayout.isPending}>
								{L.Marketing.ContentLayout.Save()}
							</Button>
						</div>
					</section>

					<section className='flex flex-col gap-2'>
						<Heading level='h2'>{L.Marketing.ContentLayout.AvailableTitle()}</Heading>
						{available.length === 0 ? (
							<Text className='text-sm text-ui-fg-subtle'>{L.Marketing.ContentLayout.NoAvailable()}</Text>
						) : (
							<div className='flex flex-col gap-1.5'>
								{available.map(content => (
									<div
										key={content.uuid}
										className='flex items-center gap-3 rounded-md border border-ui-border-base bg-ui-bg-subtle px-3 py-2.5'
									>
										<div className='flex flex-1 flex-col'>
											<Link
												to='/marketing/content/$contentUuid'
												params={{ contentUuid: content.uuid }}
												className='font-medium hover:underline'
											>
												{content.name}
											</Link>
											<div className='flex items-center gap-1.5 whitespace-nowrap text-xs leading-none text-ui-fg-subtle'>
												<span
													className={clx(
														'size-2 shrink-0 rounded-sm',
														content.status === 'PUBLISHED'
															? 'bg-ui-tag-green-icon'
															: 'bg-ui-tag-neutral-icon'
													)}
												/>
												<span>{L.Marketing.Campaign.StatusEnum(content.status)}</span>
												<span aria-hidden>·</span>
												<span>
													{L.Marketing.Content.BlockCount({ count: content.blockCount })}
												</span>
											</div>
										</div>
										<Button variant='secondary' size='small' onClick={() => add(content.uuid)}>
											<Plus className='mr-1 size-3.5' />
											{L.Marketing.ContentLayout.Add()}
										</Button>
									</div>
								))}
							</div>
						)}
					</section>
				</div>
			</div>
			{previewOpen && (
				<div className='hidden w-[360px] shrink-0 overflow-y-auto border-l border-ui-border-base bg-ui-bg-subtle p-4 [-ms-overflow-style:none] [scrollbar-width:none] lg:block [&::-webkit-scrollbar]:hidden'>
					<Text className='mb-2 text-center text-xs font-medium uppercase tracking-wide text-ui-fg-muted'>
						{L.Marketing.ContentLayout.PreviewTitle()}
					</Text>
					{feedQuery.isLoading ? (
						<Text className='text-center text-xs text-ui-fg-subtle'>{L.General.Loading()}</Text>
					) : (
						<>
							<FeedPreview items={feedQuery.data ?? []} />
							<Text className='mt-2 text-center text-[11px] text-ui-fg-muted'>
								{L.Marketing.Layout.LivePreviewHint()}
							</Text>
						</>
					)}
				</div>
			)}
		</div>
	)
}
